import {
  useState, useRef, useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@celebration/react";
import { useAuthContext } from "@auth/context/Auth.context";
import { useProcess } from "@services/process/hooks";
import {
  Button,
  Conditional,
  Flex,
  Modal,
} from "@components/index";

import styles from "./NonExecutionButton.module.scss";

interface NonExecutionButtonProps {
  event: Sentinel.Timeline.Event;
  onSuccess: () => void;
}

const ALLOWED_STATUSES: Sentinel.Enumerators.ProcessExecutionStatus[] = [
  "NOT_STARTED",
  "DELAYED",
  "STARTED",
  "STARTED_EARLY",
  "STARTED_LATE",
];

const mapStatusToPreviousStatus = (status: Sentinel.Enumerators.ProcessExecutionStatus): number => {
  const mapping: Record<string, number> = {
    "NOT_STARTED": 0,
    "DELAYED": 0,
    "STARTED": 1,
    "STARTED_EARLY": 2,
    "STARTED_LATE": 1,
  };
  return mapping[status] ?? 0;
};

const MIN_REASON_LENGTH = 10;
const EVENT_TYPE_NON_EXECUTION = 1;

const NonExecutionButton: React.FC<NonExecutionButtonProps> = ({ event, onSuccess }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasSomeResourceRole } = useAuthContext();
  const { createNonExecution } = useProcess();

  const [showForm, setShowForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [reason, setReason] = useState("");

  // Ref para manter o evento quando o modal estiver aberto
  const eventRef = useRef(event);

  // Atualiza a ref quando o event muda
  useEffect(() => {
    if (event) {
      eventRef.current = event;
    }
  }, [event]);

  const isVisible = hasSomeResourceRole("admin", "timeline-manager");
  const isNonExecution = event?.status === "NON_EXECUTION";
  const isEnabled = ALLOWED_STATUSES.includes(event?.status);
  const isReasonValid = reason.trim().length >= MIN_REASON_LENGTH;

  // Pegar último evento de não execução para exibir o motivo
  const lastNonExecutionEvent = event?.nonExecutionEvents
    ?.filter(e => e.eventType === "NON_EXECUTION")
    ?.sort((a, b) => new Date(b.eventReceivedAt).getTime() - new Date(a.eventReceivedAt).getTime())
    ?.[0];

  const handleOpenForm = () => {
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setReason("");
  };

  const handleContinue = () => {
    if (!isReasonValid) return;
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    const currentEvent = eventRef.current;

    if (!currentEvent?.id) {
      return;
    }

    try {
      await createNonExecution(currentEvent.id, {
        reason: reason.trim(),
        previousStatus: mapStatusToPreviousStatus(currentEvent.status),
        eventType: EVENT_TYPE_NON_EXECUTION,
      });

      toast.openToast({
        title: t("nonExecution.success.title"),
        description: t("nonExecution.success.description"),
        type: "positive",
        autoClose: true,
      });

      setShowConfirmModal(false);
      setShowForm(false);
      setReason("");
      onSuccess();
    } catch (error) {
      toast.openToast({
        title: t("nonExecution.error.title"),
        description: t("nonExecution.error.description"),
        type: "negative",
        autoClose: true,
      });
      setShowConfirmModal(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  if (!isVisible) {
    return null;
  }

  // Se status é NON_EXECUTION, exibir o motivo salvo
  if (isNonExecution && lastNonExecutionEvent) {
    return (
      <div className={styles.container}>
        <div className={styles.reasonDisplay}>
          <h3 className={styles.reasonTitle}>{t("nonExecution.reason.title")}</h3>
          <p className={styles.reasonText}>{lastNonExecutionEvent.reason}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Conditional condition={!showForm}>
        <Button
          label={t("nonExecution.button")}
          icon="close"
          buttonStyle="secondary"
          size="md"
          disabled={!isEnabled}
          onClick={handleOpenForm}
        />
      </Conditional>

      <Conditional condition={showForm}>
        <div className={styles.formInline}>
          <label className={styles.label}>
            {t("nonExecution.modal.label")}
          </label>
          <textarea
            className={styles.textarea}
            placeholder={t("nonExecution.modal.placeholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
          />
          <Conditional condition={reason.length > 0 && !isReasonValid}>
            <p className={styles.validation}>
              {t("nonExecution.modal.validation")}
            </p>
          </Conditional>
          <p className={styles.charCount}>
            {reason.length}/{MIN_REASON_LENGTH} {t("nonExecution.modal.charCount")}
          </p>

          <Flex className={styles.formFooter}>
            <Button
              label={t("nonExecution.modal.cancel")}
              buttonStyle="secondary"
              size="md"
              onClick={handleCloseForm}
            />
            <Button
              label={t("nonExecution.modal.continue")}
              buttonStyle="primary"
              size="md"
              disabled={!isReasonValid}
              onClick={handleContinue}
            />
          </Flex>
        </div>
      </Conditional>

      <Modal.Confirmation
        open={showConfirmModal}
        title={t("nonExecution.confirmation.title")}
        description={t("nonExecution.confirmation.description")}
        onCancel={handleCancelConfirm}
        onConfirm={() => void handleConfirm()}
      />
    </div>
  );
};

export default NonExecutionButton;
